import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, MenuItem, Radio, RadioGroup, Stack,
  Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  MANDATES, STRUCTURAL_CATEGORIES, DEFAULT_OVERLAYS, DEFAULT_MANDATE,
  PROGRESSION_MODES, DEFAULT_PROGRESSION, DATA_SOURCE, DATA_SOURCES, isKnownSymbol,
  buildSession, validateSession, DEFAULT_MANAGEMENT, SUGGESTED_MAX_BARS,
  WORKBENCH_NAME, WORKBENCH_LAUNCH, ENGINE_MODES, ENGINE_REQUIRED_MANDATE, applyEnginePreset,
} from '../utils/replaySession';
import { STRUCTURAL_COLORS } from '../utils/simulatorPalette';
import CustomDataUpload from './CustomDataUpload';
import { CUSTOM_SYMBOL, isCustomSymbol } from '../constants/customSymbol';

/**
 * THE THREE MANAGEMENT SWITCHES, declared once.
 *
 * <h2>Each hint carries its MEASURED result, and two of the three are negative</h2>
 *
 * A control offered with no figure beside it reads as an improvement nobody has got round to
 * switching on. On PTA@SPY daily only the time stop beat the control; the fan cost 32 points of
 * gross and the ATR trail 20. Both do exactly what they were built to do — each lifts the win rate
 * and shortens the hold — and on this entry vocabulary the trades they cut were worth more than
 * the losses they avoided, which is the finding rather than a reason to hide the switch.
 *
 * <p>Built from a list rather than four hand-written blocks: a lever added here appears in the
 * dialog and reaches the payload with no second edit, which is the arithmetic every multi-hop
 * lever in this repository has been bitten by.
 */
const MANAGEMENT_LEVERS = [
  {
    key: 'fanExit',
    tab: 'risk',
    label: 'Enable fan line exits',
    testId: 'mgmt-fan-exit',
    hint: 'Close on a bar through the accelerating fan — a trendline anchored at the ENTRY that '
      + 're-slopes to each new swing pivot and only ever steepens, so the break says the advance '
      + 'stopped accelerating. Measured on PTA@SPY: 183 trades, −3.73% gross against the '
      + 'control’s +28.65%.',
  },
  {
    key: 'fanReload',
    tab: 'risk',
    label: 'Enable fan line reloads',
    testId: 'mgmt-fan-reload',
    // DISABLED rather than hidden, and the hint says what would enable it. Nothing but a fan break
    // arms the retracement watch, so without the fan this control can have no effect at all -- and
    // a switch that silently does nothing is worse than one a reader can see is unavailable.
    requires: 'fanExit',
    requiresNote: 'Needs fan line exits — nothing else arms the retracement watch.',
    hint: 'After a fan break, watch for price to retrace into the broken wave’s own 38.2–61.8% '
      + 'band and close back through the fast EMA, then re-enter. Measured: 189 trades, −7.54% '
      + 'gross, so 3.8 points worse than the fan alone.',
  },
  {
    /*
     * THE ENGINE'S OWN PARAMETER PRESET, ON by default, and a SWITCH rather than an implicit rule.
     *
     * Selecting an engine selects its environment -- that is the whole point, and it is why this
     * defaults on. What it must not be is INVISIBLE: with the preset applied and no control for it,
     * this dialog would show "fan width 2, 1 tranche" while the run used width 8 and three, which
     * is the exact invisible divergence this repository has recorded three times and calls its
     * worst defect class.
     *
     * <p>It also keeps two things reachable that would otherwise be lost. The measured CONTROL
     * -- every mechanic off, which is what every published figure was taken under -- is not the
     * control if an engine selection turns four of them back on. And the fan-width sweep, whose
     * non-monotone result is on record, needs a width the preset is not overwriting.
     */
    key: 'enginePreset',
    tab: 'rules',
    label: 'Apply engine preset',
    testId: 'mgmt-engine-preset',
    /*
     * THE HINT IS DELIBERATELY TERSE, which it was not on the first attempt: spelling both presets
     * out took this tab 60px over the fold -- the zero-scroll property Phase 13 established and
     * Phase 12 had already lost once. The full table is in CLAUDE.md; what a reader needs HERE is
     * what it forces, the one figure that cuts against it, and how to turn it off.
     */
    hint: 'Run the selected engine in its intended environment, overriding the levers below. '
      + 'LEGACY: 3 tranches, fan ON at width 8, continuation ON, macro 50/200 gate ON, no '
      + 'breakeven. DIRECT: 1 tranche, fan strictly off. Worth +142 points of gross to PTA and '
      + '+589 to PTB — mostly the three tranches, so 3% at risk rather than 1% — and it RUINS CTS '
      + '(+41.42% → +2.08%). Untick to run exactly what is on screen.',
  },
  {
    /*
     * ADDITIVE, and the hint says so: it does not replace the vocabulary the mandate is running,
     * it answers for bars that vocabulary declined. A reader who read "crossover" as "instead of
     * the equator field" would expect their PTA figures to move, and they do not.
     */
    key: 'crossoverRebound',
    tab: 'rules',
    label: 'Read lines as dynamic levels',
    testId: 'mgmt-crossover',
    hint: 'Treat the Top, Equator and Bottom as levels price can lose and take back: a close '
      + 'back ACROSS one is a recapture, and a dip to one that closes back above it on an up bar '
      + 'is support holding. LONG only, and ADDITIVE — consulted only for a bar the running band '
      + 'rules declined, so no existing signal is renamed. ON by default since Phase 13, and the '
      + 'measurement is a 2x2 rather than a number: on PTA@SPY it costs 17 points of gross at ONE '
      + 'tranche (+28.65% → +11.69%) and is worth 183 at THREE (+3.54% → +194.75%), because '
      + 'these are the signals a single slot cannibalises. With the tranche count left at 1 — '
      + 'which is what this dialog opens on — the pair is the worst of the four cells.',
  },
  {
    /*
     * NOT AN EXIT, and it sits with the exits because it is the other half of one: with this on
     * the engine evaluates exits first and then offers the SAME bar to the entry rules with the
     * flattened state, so a bar can close a short and open a long.
     *
     * The hint carries both halves of the measurement because it is genuinely split, and a
     * reader turning it on for PTA deserves to know it cost 48 points of gross there.
     */
    key: 'sameBarEntry',
    tab: 'rules',
    label: 'Allow same-bar re-entry',
    testId: 'mgmt-same-bar',
    hint: 'Let one bar close a premise and open the next, instead of waiting for the following '
      + 'bar. Measured SPLIT: PTB +54.52% → +172.13% gross, PTA −12.18% → −60.77%. Entering on '
      + 'the bar a premise was stopped out is what a whipsaw IS, so whether it pays depends on '
      + 'how stale the line that fired is.',
  },
  {
    /*
     * PHASE 13, and the only lever in the GEOMETRY tab: it is not an exit and not a sensitivity,
     * it is a second way INTO the market built on the field the lines define.
     */
    key: 'chargedContinuation',
    tab: 'geometry',
    label: 'Exit-watermark continuation',
    testId: 'mgmt-continuation',
    hint: 'ANY premise stopped or fanned out arms a watch on the price it was booked out at. When '
      + 'price crosses back through that watermark and the trend filter agrees, the book retakes '
      + 'it — anywhere in the structural field, including the neutral channel between the lines. '
      + 'Off by default. Measured on PTA@SPY: 28 continuations at one tranche and 92 at three, '
      + 'worth +15.0 and +98.6 points of gross — but that is four cells on one symbol, and the '
      + 'entries underneath are the ones the forward-return test found no edge in.',
  },
  {
    /*
     * THE DECISION COMMITTEE, and the only lever here that is scoped to ONE engine.
     *
     * `onlyFor` rather than `requires`: the committee is not gated on another switch, it is
     * defined over PTB's pillars and channel and no other mode has them. A control shown on a
     * run that cannot build one would be a control that lies, which this dialog refuses
     * everywhere else -- the two band filters are hidden for CTS for the same reason.
     */
    key: 'committeeOverride',
    tab: 'risk',
    onlyFor: ['PTB_TIMING_ENGINE'],
    label: 'Decision committee override',
    testId: 'mgmt-committee',
    // THREE-VALUED UNDERNEATH: null until a reader touches it, so the mode's own default shows
    // without the dialog asserting it. `!!management[key]` would render unset as OFF and the
    // screen would then disagree with the run -- the stale-control defect, in the costume where
    // nothing on screen looks wrong.
    resolve: (m) => (typeof m.committeeOverride === 'boolean'
      ? m.committeeOverride : m.engineMode === 'PTB_TIMING_ENGINE'),
    hint: 'ON by default for this engine. When a stop fires INSIDE a PTB channel, five '
      + 'specialist managers are polled and a UNANIMOUS hold suppresses that one exit — the '
      + 'stop stays in force, so the next bar asks again. MEASURED on SPY: 53 stop breaches put '
      + 'to it, 0 held, and not one came within a single vote, so the run is identical either '
      + 'way (63 trades, −9.53%, PF 0.98). The vetoes were cycle 30, fib 31, volume 31, '
      + 'trend 17 of 36 applicable.',
  },
  {
    key: 'atrTrailingStop',
    tab: 'risk',
    label: 'Enable ATR trailing stop',
    testId: 'mgmt-atr-trail',
    hint: 'Trail the stop 2× ATR behind the best price the trade has seen — volatility-based, '
      + 'where the conforming-bar trail is structure-based. Measured: 195 trades, +8.66% gross, '
      + 'at the best win rate of the four (32.3%).',
  },
];

/**
 * THE TUNING FIELDS: how sensitive two of those mechanics are, not whether they run.
 *
 * <h2>In FRACTIONS, matching the engine and the CLI rather than the convention</h2>
 *
 * A Fibonacci retracement is conventionally quoted as a percentage — 38.2% — and these fields ask
 * for `0.382`. That is deliberate: `DynaManRuleConfig`, the request parameter and
 * `--reload-min-retracement` all take a fraction, so a reader comparing a dialog run against a CLI
 * run types the SAME number in both. A percent field here would be one more place a unit has to be
 * converted, and the conversion is only ever noticed when it is missing.
 *
 * <h2>Each is DISABLED while its mechanic is off</h2>
 *
 * All four are read on the fan and reload paths alone, so a fractal width set with the fan off
 * cannot change a single bar of the run. Same precedent as the reload switch itself: a control
 * that can have no effect is shown unavailable rather than left to look live.
 *
 * <h2>Empty is "the engine's own default", never a zero</h2>
 *
 * The placeholder is the shipped value, so a reader can see what they are departing from without
 * the field claiming to have set it. An untouched dialog therefore sends none of these — which is
 * what keeps a bare session request and a bare CLI run the same configuration.
 */
const TUNING_FIELDS = [
  {
    /*
     * HOW MANY OF THE COMMITTEE'S FOUR VOTES MUST CARRY. PTB+Timing only, and gated on the
     * committee switch above it -- a threshold for a committee that is not running is a number
     * with no effect, which this dialog refuses everywhere else.
     */
    key: 'committeeMinVotes',
    tab: 'risk',
    onlyFor: ['PTB_TIMING_ENGINE'],
    label: 'Committee votes',
    testId: 'tune-committee-votes',
    placeholder: '3',
    requires: 'committeeOverride',
    resolveRequires: (m) => (typeof m.committeeOverride === 'boolean'
      ? m.committeeOverride : m.engineMode === 'PTB_TIMING_ENGINE'),
    width: 150,
    hint: 'How many of the committee’s FOUR required votes must carry for a HOLD, 1 to 4. '
      + 'Empty means the engine default of 3 — a majority. 4 is the unanimity the committee was '
      + 'specified with and it held 0 of 53 stop breaches on SPY; 3 rescues 2 trades and 2 '
      + 'rescues 3 and makes the result WORSE (−9.51% against −9.72%). Of 36 applicable '
      + 'breaches only 2 ever reach three votes, so no threshold makes this common on that data.',
  },
  {
    key: 'fanFractalWidth',
    tab: 'risk',
    label: 'Fan pivot width',
    testId: 'tune-fan-fractal',
    placeholder: '2',
    requires: 'fanExit',
    width: 132,
    hint: 'Half-width of the swing pivots the fan anchors to, in bars. Wider needs a bigger turn '
      + 'to count as a pivot, so the line re-slopes less often and breaks later — a MACRO fan '
      + 'rather than a sensitive one. Engine default 2, i.e. a five-bar pivot. Measured on '
      + 'PTB@SPY: fan exits 85 at width 2, 51 at 3, 31 at 4, 20 at 5, and gross +2.13% / −10.69% '
      + '/ +9.17% / +40.33% — the strongest of the four, non-monotone, and four points on one '
      + 'symbol, so it is the obvious next measurement rather than a better default.',
  },
  {
    key: 'reloadEma',
    tab: 'risk',
    label: 'Confirm EMA',
    testId: 'tune-reload-ema',
    placeholder: '8',
    requires: 'fanReload',
    width: 118,
  },
  {
    /*
     * HOW MANY PREMISES THE BOOK MAY HOLD AT ONCE, and the one field here whose default is a
     * real value rather than "leave the engine alone": there is no such thing as a book with no
     * slots, so 1 IS the engine as it has always been.
     *
     * Pre-filled for the same reason the fan width is -- a reader has to be able to see what
     * their run used -- and it is the one number in this section that cannot be emptied to mean
     * anything.
     */
    key: 'maxTranches',
    tab: 'risk',
    label: 'Max concurrent tranches',
    testId: 'tune-tranches',
    placeholder: '1',
    width: 176,
    hint: 'How many independent premises the book may hold at once, 1 to 3. Each tranche keeps '
      + 'its own entry, stop and accelerating fan, and one stopping out leaves the others open. '
      + 'A new tranche is only opened by a signal the book has a free slot for, and never '
      + 'against the direction it already holds. Measured on PTA@SPY: three slots alone is '
      + 'WORSE (+28.65% → +3.54% gross), and three slots WITH the dynamic-level rules is the '
      + 'best result on record (+194.75%, net +66.5%) — because those signals were the ones a '
      + 'single slot was cannibalising.',
  },
  {
    // NOT gated on a mechanic, because it is not one: a tolerance changes which bars fire a
    // SIGNAL, so it is live on every band run whatever the exits are set to.
    key: 'continuationTrendSma',
    tab: 'geometry',
    label: 'Continuation trend SMA',
    testId: 'tune-continuation-sma',
    placeholder: '50',
    requires: 'chargedContinuation',
    width: 186,
    hint: 'The SMA length the continuation’s trend filter reads, on the signal '
      + 'timeframe. Price above it is a positive trend for a long and below it for a short, '
      + 'checked at the BREAKOUT bar rather than when the watch was armed — a continuation can '
      + 'wait indefinitely, so a trend read at arming would describe a market that may have '
      + 'rolled over since. An average too short a history to compute REFUSES.',
  },
  {
    key: 'structuralTolerance',
    tab: 'rules',
    label: 'Structural tolerance',
    testId: 'tune-tolerance',
    placeholder: '0',
    width: 158,
  },
];

/**
 * What the three reload fields buy, measured rather than guessed — so the caption can say which
 * one to reach for. PTB@SPY daily, fan and reload on, 85 fan breaks at the shipped width:
 *
 * <pre>
 *   0.382-0.618, EMA 8   (shipped)    8 reloads
 *   0.300-0.700, EMA 8                9
 *   0.200-0.800, EMA 8               14     <- the band binds harder than the EMA
 *   0.382-0.618, EMA 3               10     <- the confirmation is second
 *   watch window 20 -> 60             8     <- does not bind at all, so it is not offered
 * </pre>
 *
 * <p>And the FAN WIDTH dominates both, because it decides how many breaks there are to arm a
 * watch at all: at width 5 the fan breaks 20 times and reloads NEVER.
 */
const RELOAD_SENSITIVITY = 'The reload is BINARY now: a fan exit inside a live structural window '
  + 'arms it, and a close back ACROSS this EMA fires it. The 38.2–61.8% retracement band is '
  + 'gone — it was the binding term (widening it to 0.200–0.800 took 8 reloads to 14), which is '
  + 'exactly the objection to it: a gate whose width nobody derived fires when the width happens '
  + 'to fit. A CROSS and not a side, so a watch armed while price is already above the average '
  + 'does not re-enter on its first bar.';

/**
 * What the tolerance is for, in the field's own caption.
 *
 * <p>Separate from the reload prose because it is a different mechanic: the reload is an exit
 * consequence and this changes which bars raise a SIGNAL at all.
 */
const TOLERANCE_NOTE = 'How close to the Top, Bottom or Equator line counts as reaching it, in '
  + 'ATRs. 0 is the strict rule — a mathematically exact cross — which is what every published '
  + 'figure was measured under and is also what makes a rally that stalls three cents under the '
  + 'line and turns fire nothing at all. The widening is applied to whichever side price is '
  + 'approaching from, so it can only ever make a structural condition easier to meet.';

/**
 * THE SESSION SETUP: the window, the canvas, and the engine — decided before anything is fetched.
 *
 * <h2>Why a modal in front of the run rather than controls on the transport</h2>
 *
 * Three of these four choices cannot be changed without re-fetching: the date window re-indexes
 * the session, and the structural overlays decide which band tables have to be requested. A
 * control that silently re-ran the engine every time it was touched would be a dialog pretending
 * not to be one. So the irreversible choices are made here, together, once — and the one that IS
 * live, the mandate, also appears on the transport.
 *
 * <h2>The dates bound the SESSION, not the run</h2>
 *
 * The engine still replays its whole history, because a 200-period average cannot be computed from
 * the middle of a series. What the window changes is which bars the transport walks and which
 * trades the ALGO vs HUMAN comparison counts. That is stated on screen, because a date picker on a
 * simulator reads as "only analyse this period" and the engine's decisions are not narrowed by it.
 */

/** The widest window a native date input will offer, from the chart's own series. */
/**
 * THE THREE PARAMETER TABS, declared once.
 *
 * <h2>Grouped by the QUESTION each answers, not by when each was built</h2>
 *
 * Which is why the mandate sits with the entry rules rather than with the overlays it used to be
 * drawn beside: picking PTB and ticking "read lines as dynamic levels" are two halves of one
 * decision about what counts as a signal, while the exits are a decision about what happens
 * afterwards.
 *
 * <p>A lever's tab is a property of the LEVER (`tab:` on each entry above), so adding one needs no
 * edit here -- the same rule the fields already follow. This list carries only what is true of the
 * tab itself: its label, its one-line statement of scope, and the notes too long to sit under a
 * single control.
 *
 * <p><b>Band low / high are deliberately absent from Risk &amp; management.</b> The brief lists
 * them, and they no longer exist: Phase 10 replaced the fractional reload band with the binary
 * confirmation, and `reloadMinRetracement` / `reloadMaxRetracement` are now REFUSED by the API with
 * an explanation rather than ignored. Re-adding fields for them would put two controls on screen
 * that the backend answers 400 to.
 */
const PARAM_TABS = [
  {
    key: 'rules',
    label: 'Mandate & rules',
    blurb: 'Which engine the algo book trades, and what counts as a signal from it.',
    notes: [TOLERANCE_NOTE],
  },
  {
    key: 'risk',
    label: 'Risk & management',
    blurb: 'What happens to a premise once it is open: the exits, their sensitivities, and how '
      + 'many premises the book may hold at once. Every published figure was measured with the '
      + 'four exits OFF, and on PTA only the time stop beat that control.',
    notes: [
      TUNING_FIELDS.find((f) => f.key === 'fanFractalWidth').hint,
      RELOAD_SENSITIVITY,
      TUNING_FIELDS.find((f) => f.key === 'maxTranches').hint,
    ],
  },
  {
    key: 'geometry',
    label: 'Geometry extensions',
    blurb: 'Phase 13: the structural lines persist from the end of one cycle to the start of the '
      + 'next, so everything here is evaluable through the VOID between bands as well as beside '
      + 'them — and the continuation applies across the whole field, not only beyond the lines.',
    notes: [TUNING_FIELDS.find((f) => f.key === 'continuationTrendSma').hint],
  },
];

const boundsOf = (dates) => {
  const axis = Array.isArray(dates) ? dates : [];
  if (axis.length === 0) return { min: null, max: null };
  return { min: axis[0], max: axis[axis.length - 1] };
};

/**
 * THE SYMBOL'S OWN RANGE: its first available date to its last.
 *
 * <h2>It used to be the last 500 bars, and that was a different bet</h2>
 *
 * The reasoning was that the whole series is "a 33-year session nobody will finish" — true when
 * play stopped on all 142 trade entries and the window slid a bar at a time. Continuous playback
 * and threshold panning are what make the whole range watchable, and a window the operator did
 * not choose is a window they have to undo: reading the symbol's metadata and offering all of it
 * states what is available and leaves shortening it to them, which is the way round that cannot
 * silently hide the early part of a series.
 *
 * <p>`dates` IS the metadata. It is the axis of the series the chart has loaded for this symbol,
 * so its two ends are that symbol's first and last available date by construction — and they
 * cannot disagree with what a session would actually replay, which a separately fetched
 * first/last pair could.
 */
export function defaultWindow(dates) {
  const axis = Array.isArray(dates) ? dates : [];
  if (axis.length === 0) return { startDate: null, endDate: null };
  return { startDate: axis[0], endDate: axis[axis.length - 1] };
}

/** The floor for an uploaded series, which has no catalogue entry to read a range from. */
export const EXTERNAL_START_DATE = '1901-01-01';

/**
 * Today, as the local calendar has it.
 *
 * <p>LOCAL rather than UTC, deliberately, and it is the one place in this repository where that
 * is the right choice: the value is going into a native date input, which is a local-calendar
 * control, and an operator west of UTC who was shown tomorrow's date would be looking at a
 * mistake. `toISOString()` here would be the bug rather than the fix.
 */
export function todayLocal(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The window an EXTERNAL series opens on: 1901 to today.
 *
 * <p>A CSV has no catalogue entry, so there is no first and last date to read until it has been
 * parsed — and the widest possible range is the only default that cannot exclude part of a file
 * nobody has seen yet. `frameBounds` snaps both ends inward to the frames that actually exist,
 * so a 1901 start on a series beginning in 1987 is the series' own start rather than an error.
 */
export function externalWindow(now = new Date()) {
  return { startDate: EXTERNAL_START_DATE, endDate: todayLocal(now) };
}

/**
 * Spelled out, because the acronyms are not self-explanatory to anyone but their author.
 *
 * <p>Declared ABOVE its consumer: a const below the component that reads it is the temporal dead
 * zone this repository has taken the whole app down with once.
 */
export const OVERLAY_LABELS = {
  PTA: 'PTA — Price Time Analysis A',
  PTB: 'PTB — Price Time Analysis B',
  CTS: 'CTS — Celestial Timing Series',
};

export default function SimulatorSetupDialog({
  open, onClose, onLaunch, symbol, symbols, onSymbolChange,
  onCustomSeries, onClearCustomSeries, dates, launching, error,
}) {

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [overlays, setOverlays] = useState({ ...DEFAULT_OVERLAYS });
  const [mandate, setMandate] = useState(DEFAULT_MANDATE);
  const [progression, setProgression] = useState(DEFAULT_PROGRESSION);
  /**
   * THE TRADE-MANAGEMENT BLOCK, held as one object rather than four pieces of state.
   *
   * <p>It is passed to the session whole and normalised there, so the dialog never has to agree
   * with the payload about what "off" is. Starts at `DEFAULT_MANAGEMENT`, which is the measured
   * control — a session that ignores this section runs the configuration every published figure
   * was taken with.
   */
  const [management, setManagement] = useState({ ...DEFAULT_MANAGEMENT });

  /*
   * WHICH PARAMETER TAB IS OPEN -- a view over one form, never a filter on what is sent.
   *
   * It opens on `rules` because the mandate lives there and it is the most consequential choice
   * in the section: it decides which engine the algo book trades, and the other two tabs only
   * adjust how. Deliberately NOT reset when the dialog reopens -- a reader who was working in
   * Risk & management and closed the dialog to look at the chart is still working there.
   */
  const [paramTab, setParamTab] = useState(PARAM_TABS[0].key);

  /**
   * WHETHER THE BARS GO TO THE ENGINE.
   *
   * <p>Only ever offered for an uploaded series. For a stored symbol the backend already has the
   * data, so there is nothing to withhold and nothing to decide; defaulting an upload to OFF is
   * the conservative reading of "proprietary data never needs to touch the backend", and the
   * switch is what makes it a decision rather than a surprise either way.
   */
  const [engine, setEngine] = useState(false);
  const custom = isCustomSymbol(symbol);

  /**
   * WHICH SOURCE the dialog is configured for.
   *
   * <p>Seeded from the chart's current symbol on OPEN, so a reader who already has an upload
   * loaded finds the dialog on External rather than having to say so again — and one on SPY finds
   * it on Internal. Changing it CHANGES THE CHART, because the symbol does: picking External
   * selects the synthetic symbol, and picking Internal puts the last real one back.
   */
  const [source, setSource] = useState(custom ? DATA_SOURCE.EXTERNAL : DATA_SOURCE.INTERNAL);

  /**
   * What the date inputs will OFFER.
   *
   * <p>DECLARED BELOW `source`, which it reads. In its natural reading position — next to the
   * other derived values at the top — it put `source` in the temporal dead zone and threw on
   * every render.
   *
   * <p>For a stored symbol, the series' own two ends — a native date input greys out everything
   * outside `min`/`max`, so this is what stops a session being asked for bars that do not exist.
   *
   * <p>For an upload it has to widen, and the reason is the default itself: the external window
   * starts at 1901, which on a chart still holding the previous symbol's axis would be below
   * `min` and would open the dialog with its own default flagged as out of range.
   */
  const seriesBounds = useMemo(() => boundsOf(dates), [dates]);
  const { min, max } = source === DATA_SOURCE.EXTERNAL
    ? { min: EXTERNAL_START_DATE, max: todayLocal() }
    : seriesBounds;

  /**
   * THE TEXT IN THE BOX, which is NOT the same thing as the chart's symbol.
   *
   * <p>Found in a browser: typing `ZZZZ` left the chart on SPY — correctly, since an unknown
   * ticker must not be selected — and the validation, which read the CHART's symbol, therefore
   * saw SPY and let Launch through. The field said one thing and the session would have run
   * another, which is worse than either refusing or accepting.
   *
   * <p>So the typed text is tracked separately and is what gets validated. Null means "nothing
   * has been typed since this opened", in which case the chart's own symbol is the answer.
   */
  const [typed, setTyped] = useState(null);
  /** The last real ticker, so leaving External has somewhere to go back to. */
  const lastInternal = useRef(custom ? null : symbol);
  /** Which symbol (or EXTERNAL) the date window currently shown was seeded from. */
  const seededFor = useRef(null);
  useEffect(() => { if (!custom && symbol) lastInternal.current = symbol; }, [custom, symbol]);

  /*
   * THE WINDOW IS RE-SEEDED ON OPEN, not once at mount.
   *
   * A `useState` initialiser runs exactly once, and this dialog stays mounted for the life of the
   * page — so after the first open it would hold the window of whatever symbol was loaded then,
   * while the chart had moved on. That is the defect the DynaMan dialog's symbol field had: a
   * reader on QQQ pressed Run and got SPY.
   *
   * On OPEN and not on every `dates` change, so a deliberately typed window survives a redraw.
   */
  useEffect(() => {
    if (!open) return;
    seededFor.current = null;              // whatever is open now re-seeds below
    setSource(isCustomSymbol(symbol) ? DATA_SOURCE.EXTERNAL : DATA_SOURCE.INTERNAL);
    setTyped(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on OPEN, deliberately
  }, [open, symbol]);

  /**
   * THE WINDOW FOLLOWS THE SYMBOL — once per symbol, not once per redraw.
   *
   * <h2>Two defaults, because the two sources know different things</h2>
   *
   * <pre>
   *   Internal   the chosen symbol's own first and last date, read off the series the chart loaded
   *   External   1901-01-01 to today, because a file nobody has parsed yet has no range to read
   * </pre>
   *
   * <h2>Keyed on the symbol, which is what makes it reactive WITHOUT being destructive</h2>
   *
   * Seeding on every `dates` change would overwrite a window the operator had deliberately typed
   * the next time anything redrew the chart. Seeding only on open — which is what this used to do
   * — leaves the dates belonging to the previous symbol the moment they pick a new one, and the
   * session then runs a window measured against another instrument's calendar.
   *
   * <p>So the seed is remembered against the symbol it came from. A new symbol re-seeds exactly
   * once, when its bars arrive; a redraw of the same symbol does not.
   */
  useEffect(() => {
    if (!open) return;
    const key = source === DATA_SOURCE.EXTERNAL ? `${DATA_SOURCE.EXTERNAL}` : (symbol || '');
    if (seededFor.current === key) return;

    if (source === DATA_SOURCE.EXTERNAL) {
      const w = externalWindow();
      seededFor.current = key;
      setStartDate(w.startDate);
      setEndDate(w.endDate);
      return;
    }
    // Internal: wait for the bars. Until they land there is no first and last date to read, and
    // seeding from the PREVIOUS symbol's axis is the defect this is here to prevent.
    if (!Array.isArray(dates) || dates.length === 0) return;
    const w = defaultWindow(dates);
    seededFor.current = key;
    setStartDate(w.startDate);
    setEndDate(w.endDate);
  }, [open, source, symbol, dates]);

  /**
   * Switching source switches the chart's symbol, which is what makes the choice real.
   *
   * <p>External selects the synthetic symbol so the upload panel has somewhere to put its bars;
   * Internal puts the last real ticker back. Doing nothing and waiting for Launch would leave the
   * dialog claiming one source while the chart held the other.
   */
  const chooseSource = (value) => {
    setSource(value);
    if (!onSymbolChange) return;
    if (value === DATA_SOURCE.EXTERNAL && !isCustomSymbol(symbol)) onSymbolChange(CUSTOM_SYMBOL);
    if (value === DATA_SOURCE.INTERNAL && isCustomSymbol(symbol) && lastInternal.current) {
      onSymbolChange(lastInternal.current);
    }
  };

  const toggle = (category) => setOverlays((current) => {
    const next = { ...current, [category]: !current[category] };
    return next;
  });

  /*
   * TICKING A MANDATE TICKS ITS OVERLAY.
   *
   * Trading an engine whose structure is not on the canvas is a session with nothing to audit
   * against — the markers would appear on bars with no visible reason. The reverse is NOT forced:
   * an overlay may be drawn without being traded, which is the whole point of separating
   * visualisation from execution.
   */
  const chooseMandate = (value) => {
    setMandate(value);
    setOverlays((current) => (current[value] ? current : { ...current, [value]: true }));
  };

  /**
   * UNTICKING THE FAN CLEARS THE RELOAD.
   *
   * <p>The reload's control is disabled without the fan, so leaving it ticked would park a flag
   * the operator can neither see the effect of nor reach — and the payload would then either carry
   * a reload with no fan (which arms nothing) or silently re-enable the fan to honour it. Clearing
   * it makes the screen and the request say the same thing.
   */
  const setLever = (key, value) => {
    setManagement((current) => {
      /*
       * THE ENGINE AND ITS PRESET ARE ONE DECISION, so both reroute through the same applier.
       *
       * Selecting an engine WRITES the preset's values into the controls below rather than leaving
       * the backend to apply them silently -- otherwise this dialog would show "1 tranche, fan
       * width 2" on a run that used 3 and 8, which is the invisible divergence CLAUDE.md records
       * three times as this repository's worst defect class. Unticking writes the control's values
       * back, which is what keeps the measured baseline reachable from here.
       *
       * The two levers the preset forces that this dialog has no control for -- the macro 50/200
       * gate and the breakeven stop -- are named in the switch's own hint, because they are the
       * part that genuinely cannot be shown.
       */
      if (key === 'engineMode') {
        /*
         * AN ENGINE THAT REQUIRES A MANDATE TAKES IT, and the Timing Engine is the only one.
         *
         * Its forced rules are read by the CTS provider alone, so a Timing run on a PTA mandate
         * would be an ordinary Legacy band run with every distinguishing rule inert -- and the
         * BACKEND refuses that outright, so without this the reader's next Launch would be a 400
         * rather than a run. Switched here, where the engine is chosen, so the two dropdowns
         * cannot be left disagreeing.
         *
         * Setting state from inside another setter's updater is deliberate and safe: `setMandate`
         * is a different store, React batches both into one render, and the alternative -- doing
         * it in the onChange handler -- would put the rule two places from the engine it belongs
         * to.
         */
        const required = ENGINE_REQUIRED_MANDATE[value];
        if (required) {
          chooseMandate(required);
        }
        // SELECTING AN ENGINE TURNS THE PRESET ON, which is the brief's own clause: the engine's
        // intended environment is what a selection asks for. It overrides a reader who had switched
        // it off, deliberately -- they chose an engine after that, and a selection that left the
        // previous engine's levers in place would be the comparison the preset exists to prevent.
        return applyEnginePreset(current, value, true);
      }
      if (key === 'enginePreset') {
        return applyEnginePreset(current, current.engineMode, value);
      }
      const next = { ...current, [key]: value };
      if (key === 'fanExit' && value === false) next.fanReload = false;
      return next;
    });
  };

  /**
   * NOT READY until the chosen symbol's bars are on the chart.
   *
   * <p>The symbol takes effect immediately, but `barData` loads asynchronously — so for a moment
   * the modal holds a symbol whose dates it does not have. Launching there would fetch a timeline
   * for the new symbol and bound it to a window measured against the previous one's calendar.
   *
   * <p>For Z-Custom the same state means "no file parsed yet", which is the honest reading: the
   * series does not exist until the operator provides it.
   */
  const waitingForBars = !Array.isArray(dates) || dates.length === 0;

  const session = buildSession({
    startDate, endDate, overlays, mandate, progression, symbol,
    // A STORED SYMBOL ALWAYS RUNS THE ENGINE. The switch exists for the privacy question, which
    // only an uploaded series has -- and forcing it true here means a reader who uploaded, chose
    // private, then switched back to SPY cannot accidentally run SPY with no algo column.
    engine: custom ? engine : true,
    management,
  });
  /**
   * THE TICKER IS VALIDATED AT SUBMISSION, not at every keystroke.
   *
   * <p>Rejecting a character as it is typed makes a search box unusable — "SP" is not a symbol
   * and is on the way to one. So the field accepts anything and the LAUNCH is what is blocked,
   * with the reason on screen. That also means the check can say "not in the catalogue" rather
   * than just refusing to register the key.
   *
   * <p>Order matters: an unknown ticker is reported before "loading", because a reader who has
   * typed nonsense should be told that rather than watching a spinner for a symbol that will
   * never arrive.
   */
  /** What the operator is asking for: what they typed, or the chart's symbol if they have not. */
  const wanted = typed !== null ? typed : (symbol || '');
  const unknownTicker = source === DATA_SOURCE.INTERNAL
    && !isCustomSymbol(wanted)
    && !!wanted
    && !isKnownSymbol(wanted, symbols);

  const problem = unknownTicker
    ? `${wanted} is not in the symbol catalogue — pick one from the list.`
    : (source === DATA_SOURCE.INTERNAL && typed !== null && typed === '')
      ? 'Type a symbol, or pick one from the list.'
      : waitingForBars
        ? (source === DATA_SOURCE.EXTERNAL
          ? 'Upload a CSV — the session runs on bars from your own file.'
          : `Loading ${symbol || 'the symbol'}…`)
        : validateSession(session);

  const extras = STRUCTURAL_CATEGORIES.filter((c) => overlays[c] && c !== mandate);

  return (
    /*
     * `lg`, NOT `md`, and the brief offered either.
     *
     * At md the dialog is 900px and each column is about 420 — narrower than the 600 this
     * content was written at, so every caption in it reflows LONGER and the two columns grow
     * back most of the height the split was for. At lg each column is ~560, which is within
     * 40px of what a reader sees today, so the heights stay close to what was measured.
     *
     * `fullWidth` keeps it a maximum rather than a width: on a 1024px screen the dialog is the
     * viewport less its margins, and below the md breakpoint the grid stacks back to one column.
     */
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {`${WORKBENCH_NAME} Setup`}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {symbol ? `${symbol} — ` : ''}
          choose the session window, what the canvas draws, and which engine the algo book trades.
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {/*
          TWO COLUMNS, so the whole configuration is visible at once.
          ===========================================================

          <p>In one column this dialog was 1,426px of content in a 1,083px window: the Trade
          management section -- the last one added, and the only one whose every option is off --
          sat entirely below the fold, so an operator had to scroll to discover it existed.

          <p>The split is BY KIND rather than by length, and PHASE 13 MOVED THE LINE: the left
          column is WHICH BARS (the source and the window) and the right is HOW THEY ARE WATCHED
          (what the canvas draws, and whether the transport advances itself). Everything about
          what the ENGINE does with them is in the tab strip below.

          <p>It moved because the right column's identity changed anyway when the mandate left it
          for the first tab -- and because the two halves had become 503px against 204, which is a
          split by kind that had stopped being a split of anything. Progression mode reads as
          "how you watch" at least as naturally as "what the session is", so it is the block that
          moves.

          <p>It collapses to one column below `md`, which is the layout this dialog had before --
          two 420px columns of this much prose is worse than a scrollbar.
        */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 3,
            rowGap: 1,
            // TOP-ALIGNED. The columns are different heights by construction and `stretch`
            // would make the shorter one's divider run to the bottom of the taller one.
            alignItems: 'start',
          }}
        >
          <Box>

            {/* ── THE DATA SOURCE ─────────────────────────────────────────────────────────────
                Asked explicitly rather than hidden as one row at the top of an eight-hundred-row
                dropdown. The two modes need different controls and ask different questions — one
                validates a ticker, the other asks whether the bars may be sent anywhere — and a
                single list that silently changes the rest of the dialog when one of its rows is
                picked is a mode pretending not to be one. */}
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Data source</Typography>
            <RadioGroup
              row
              value={source}
              onChange={(e) => chooseSource(e.target.value)}
            >
              {DATA_SOURCES.map((m) => (
                <FormControlLabel
                  key={m.value}
                  value={m.value}
                  disabled={!onSymbolChange || !!launching}
                  control={<Radio size="small"
                    inputProps={{ 'data-testid': `source-${m.value.toLowerCase()}` }} />}
                  label={<Typography variant="body2">{m.label}</Typography>}
                />
              ))}
            </RadioGroup>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              {DATA_SOURCES.find((m) => m.value === source)?.hint}
            </Typography>

            {source === DATA_SOURCE.INTERNAL ? (
              <>
                {/* TYPE-AHEAD over the catalogue, and the SUBMISSION is what is validated.
                    `freeSolo` is on so the field behaves like a search box rather than a picker —
                    a reader who knows the ticker types it and presses Launch — and the validity
                    check lives in `problem` below, where it can say WHY rather than silently
                    refusing to accept a keystroke. */}
                <Autocomplete
                  freeSolo
                  size="small"
                  options={Array.isArray(symbols) ? symbols.filter((x) => x && !isCustomSymbol(x)) : []}
                  value={custom ? '' : (symbol || '')}
                  onChange={(_, picked) => {
                    if (picked && onSymbolChange) onSymbolChange(String(picked).trim().toUpperCase());
                  }}
                  onInputChange={(_, value, reason) => {
                    // `reason === 'reset'` fires when the value is set programmatically; acting on it
                    // would re-select the symbol we have just been given and loop.
                    if (reason !== 'input') return;
                    const name = String(value || '').trim().toUpperCase();
                    setTyped(name);
                    // ONLY A KNOWN TICKER CHANGES THE CHART. Loading on every keystroke would fetch
                    // "S", "SP", "SPY"; loading on an unknown one would ask the backend for a symbol
                    // it does not have. What an unknown one DOES do is block Launch, below.
                    if (onSymbolChange && name && isKnownSymbol(name, symbols)) onSymbolChange(name);
                  }}
                  disabled={!onSymbolChange || !!launching}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Symbol"
                      placeholder="Type a ticker"
                      inputProps={{ ...params.inputProps, 'data-testid': 'session-symbol' }}
                    />
                  )}
                />
                {!Array.isArray(symbols) || symbols.length === 0 ? (
                  <Typography variant="caption"
                    sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                    {/* SAID, rather than silently letting anything through. The catalogue is fetched
                        lazily and a failure is non-fatal by design; treating "we could not ask" as
                        "the answer is no" would make a network blip look like a typo and block every
                        session. */}
                    The symbol catalogue could not be loaded, so the ticker cannot be checked here —
                    the backend will refuse an unknown one with its own message.
                  </Typography>
                ) : null}
              </>
            ) : (
              <Box data-testid="session-upload">
                {/* THE EXISTING UPLOAD COMPONENT, not a second file input. It reads the file with
                    `FileReader`, parses in memory, drops the text and persists NOTHING — no
                    localStorage, no IndexedDB, no retained File handle. A second implementation
                    would be a second chance to get that wrong, and the thing being got wrong would
                    be a hedge fund's price history on their analyst's laptop. */}
                <CustomDataUpload onSeriesReady={onCustomSeries} onClear={onClearCustomSeries}
                  disabled={!!launching} />

                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={(
                    <Switch
                      size="small"
                      checked={engine}
                      onChange={() => setEngine((v) => !v)}
                      inputProps={{ 'data-testid': 'session-engine' }}
                    />
                  )}
                  label={(
                    <Typography variant="body2">
                      Run the engine on these bars
                    </Typography>
                  )}
                />
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {/* THE TRADE, STATED. There is no way to have the engine's opinion of a private
                      series without sending the series, and a reader deciding about their own data
                      is entitled to know that before they decide. */}
                  {engine
                    ? 'The parsed bars are POSTed to the simulation endpoint so the engine can price '
                      + 'them — that is the only way to get an algo book to compare against. The CSV '
                      + 'file itself is never uploaded; only the parsed bars are.'
                    : 'Nothing is sent. The chart unveils, your own book trades, and the algo column '
                      + 'stays empty — there is no algorithm, because no engine ran. PTA/PTB bands '
                      + 'need the engine too, so the canvas will draw only what you can see.'}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Session window</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start"
                type="date"
                size="small"
                fullWidth
                value={startDate || ''}
                onChange={(e) => setStartDate(e.target.value || null)}
                inputProps={{ min, max, 'data-testid': 'session-start' }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End"
                type="date"
                size="small"
                fullWidth
                value={endDate || ''}
                onChange={(e) => setEndDate(e.target.value || null)}
                inputProps={{ min, max, 'data-testid': 'session-end' }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
              {/* SAID OUT LOUD, because a date picker on a simulator reads as "only analyse this
                  period" and that is not what it does. The engine needs its whole history; what the
                  window bounds is the walk and the comparison. */}
              Any window the series covers, up to the whole of it
              {min && max ? ` (${min} to ${max})` : ''}. The engine still replays its full history —
              it has to, for its own averages — so the window bounds which bars the transport walks and
              which trades the ALGO vs HUMAN comparison counts.
            </Typography>


          </Box>

          <Box
            sx={{
              // A RULE BETWEEN THE COLUMNS, which becomes a rule ABOVE the second one when they
              // stack. Without it the two columns read as one list that happens to wrap.
              pl: { xs: 0, md: 3 },
              pt: { xs: 2, md: 0 },
              borderLeft: { xs: 0, md: '1px solid' },
              borderTop: { xs: '1px solid', md: 0 },
              borderColor: 'divider',
            }}
          >

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Signal overlays</Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
              Structural pillars stand across the whole axis from the start, including the empty space
              ahead of the price — they are calendar, so drawing them early reveals nothing. The price
              bars then animate into them.
            </Typography>
            <Stack>
              {STRUCTURAL_CATEGORIES.map((category) => (
                <FormControlLabel
                  key={category}
                  control={(
                    <Checkbox
                      size="small"
                      checked={!!overlays[category]}
                      onChange={() => toggle(category)}
                      inputProps={{ 'data-testid': `overlay-${category.toLowerCase()}` }}
                      sx={{
                        // THE CHECKBOX WEARS THE PILLAR'S OWN COLOUR, so the modal and the chart
                        // agree without a legend. One palette, read in two places.
                        color: STRUCTURAL_COLORS[category],
                        '&.Mui-checked': { color: STRUCTURAL_COLORS[category] },
                      }}
                    />
                  )}
                  label={(
                    <Typography variant="body2">
                      {OVERLAY_LABELS[category]}
                      {category === mandate ? (
                        <Typography component="span" variant="caption"
                          sx={{ ml: 0.75, color: 'text.secondary' }}>
                          — traded by the algo book
                        </Typography>
                      ) : null}
                    </Typography>
                  )}
                />
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* ── PROGRESSION MODE ────────────────────────────────────────────────────────────
                A MODE and not a speed of zero: `clampDelay` has a 1ms floor precisely so a zero
                cannot become a busy loop that locks the tab, and in Manual the play button is not
                slow, it is absent. A control that looked available and did nothing would be worse
                than one that is visibly not offered. */}
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Progression mode</Typography>
            <RadioGroup
              value={progression}
              onChange={(e) => setProgression(e.target.value)}
            >
              {PROGRESSION_MODES.map((m) => (
                <Box key={m.value} sx={{ mb: 0.25 }}>
                  <FormControlLabel
                    value={m.value}
                    control={<Radio size="small"
                      inputProps={{ 'data-testid': `progression-${m.value.toLowerCase()}` }} />}
                    label={<Typography variant="body2">{m.label}</Typography>}
                  />
                  <Typography variant="caption"
                    sx={{ display: 'block', ml: 3.75, color: 'text.secondary' }}>
                    {m.hint}
                  </Typography>
                </Box>
              ))}
            </RadioGroup>

          </Box>


          {/*
            THE PARAMETERS, IN TABS -- the Phase 13 answer to a dialog that kept growing downward.
            ============================================================================

            <p>Every phase since 6C has appended a control to this dialog, and each time the cost
            was vertical: Phase 12 measured 1,810px of content in a 1,083px window with the newest
            field below the fold, and fixed half of it by spanning the heaviest section across both
            columns (1,810 -> 1,314). That bought one phase of headroom and no more.

            <p><b>Tabs change the axis the dialog grows along.</b> A fourth group of parameters now
            costs a tab rather than another screenful, so the layout stops degrading with each
            addition instead of degrading more slowly.

            <h2>The three groups are by QUESTION, which is why the mandate is in the first one</h2>

            <ul>
              <li><b>Mandate &amp; rules</b> -- what counts as a signal: which engine trades, and
                  the two switches plus the tolerance that decide whether a bar fires one.</li>
              <li><b>Risk &amp; management</b> -- what happens to a premise once it is open: the
                  exits, their sensitivities, and how many premises the book may hold.</li>
              <li><b>Geometry extensions</b> -- what Phase 13 added on top of the field the lines
                  define.</li>
            </ul>

            <p>The session blocks ABOVE stay out of the tabs deliberately. They are answered once,
            and burying the date window behind a tab would mean a reader could launch a run without
            ever seeing the window it covers.

            <h2>Every panel stays MOUNTED, hidden rather than unmounted</h2>

            <p>This is one form with three views, not three forms: the values of a tab nobody opened
            still travel on the wire, because they are the engine's configuration and not this
            component's. Unmounting would also make `management` the only record of a control's
            value while its tab is closed, which is a second place for the two to disagree.
          */}
          <Box
            data-testid="param-tabs"
            sx={{
              gridColumn: { md: '1 / -1' },
              mt: 2,
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Tabs
              value={paramTab}
              onChange={(_, next) => setParamTab(next)}
              // FULL WIDTH, so the three are a segmented control across the dialog rather than
              // three words huddled at the left edge of a 1,113px row.
              variant="fullWidth"
              sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
            >
              {PARAM_TABS.map((t) => (
                <Tab
                  key={t.key}
                  value={t.key}
                  label={t.label}
                  id={`param-tab-${t.key}`}
                  aria-controls={`param-panel-${t.key}`}
                  data-testid={`param-tab-${t.key}`}
                />
              ))}
            </Tabs>

            {PARAM_TABS.map((t) => {
              const active = t.key === paramTab;
              const levers = MANAGEMENT_LEVERS.filter((l) => l.tab === t.key);
              const fields = TUNING_FIELDS.filter((f) => f.tab === t.key
                && (!f.onlyFor || f.onlyFor.includes(management.engineMode)));
              return (
                <Box
                  key={t.key}
                  role="tabpanel"
                  hidden={!active}
                  id={`param-panel-${t.key}`}
                  aria-labelledby={`param-tab-${t.key}`}
                  data-testid={`param-panel-${t.key}`}
                  // `display: none` AND the `hidden` attribute: the first is what gives a closed
                  // panel zero height, which is the whole point, and the second is what a screen
                  // reader and a test can both read.
                  sx={{ display: active ? 'block' : 'none', pt: 1.5 }}
                >
                  <Typography variant="caption"
                    sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                    {t.blurb}
                  </Typography>

                  {/*
                    THE EXECUTION LAYER, FIRST IN THE FIRST TAB.
                    ===========================================

                    <p>It is the largest choice in this dialog -- it decides which of two entirely
                    different rule sets runs -- so it sits above the mandate rather than beside the
                    sensitivities. A reader who changes it has changed more than any other control
                    here can.

                    <p>It is in "Mandate & rules" because that tab answers "what counts as a
                    signal and who trades it", and this is the second half of that question.
                  */}
                  {/* THE TWO "WHO TRADES" SELECTS SIDE BY SIDE, which is what keeps this tab
                      above the fold. Stacked they were 118px over on a full-height window -- the
                      zero-scroll property Phase 13 established, lost to one control. They are the
                      same shape and answer halves of one question, so pairing them reads better
                      than it did stacked as well as fitting. One column below md. */}
                  {t.key === 'rules' ? (
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      columnGap: 3,
                      rowGap: 1,
                      alignItems: 'start',
                      mb: 1.5,
                    }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Execution engine</Typography>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={management.engineMode}
                        onChange={(e) => setLever('engineMode', e.target.value)}
                        // NOT `session-engine`: that id already belongs to the CSV-upload privacy
                        // switch ("run the engine on my bars"), and reusing it made an existing
                        // test find this dropdown instead. Two controls, two ids.
                        inputProps={{ 'data-testid': 'session-execution-engine' }}
                      >
                        {ENGINE_MODES.map((m) => (
                          <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                        ))}
                      </TextField>
                      <Typography variant="caption"
                        sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
                        {ENGINE_MODES.find((m) => m.value === management.engineMode)?.hint}
                      </Typography>
                    </Box>
                    <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Algo mandate</Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={mandate}
                onChange={(e) => chooseMandate(e.target.value)}
                inputProps={{ 'data-testid': 'session-mandate' }}
              >
                {MANDATES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
                {MANDATES.find((m) => m.value === mandate)?.hint}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
                {/* THE INDEPENDENCE, stated where the choice is made. */}
                The algo book evaluates this engine and nothing else. The canvas keeps drawing every
                overlay ticked above, and your own trades are free to act on any of them.
                {extras.length ? ` Drawing ${extras.join(' and ')} as well costs one extra request each,`
                  + ' because a band\'s levels are the engine\'s own.' : ''}
              </Typography>
                    </Box>
                    </Box>
                  ) : null}

                  {/* TWO COLUMNS of switches at full width, which is where the height comes back:
                      stacked rows of a long hint become half as many. One column below md, as the
                      dialog itself does -- 556px halved is narrower than this prose reads at. */}
                  {levers.length ? (
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      columnGap: 3,
                      rowGap: 0.75,
                    }}>
                      {levers.filter((lever) => !lever.onlyFor
                        || lever.onlyFor.includes(management.engineMode)).map((lever) => {
                        const blocked = !!lever.requires && !management[lever.requires];
                        const on = lever.resolve
                          ? lever.resolve(management) : !!management[lever.key];
                        return (
                          <Box key={lever.key}>
                            <FormControlLabel
                              sx={{ mr: 0 }}
                              control={(
                                <Switch
                                  size="small"
                                  checked={on && !blocked}
                                  disabled={blocked}
                                  onChange={(e) => setLever(lever.key, e.target.checked)}
                                  inputProps={{ 'data-testid': lever.testId }}
                                />
                              )}
                              label={<Typography variant="body2">{lever.label}</Typography>}
                            />
                            <Typography variant="caption"
                              sx={{ display: 'block', ml: 6, mt: -0.25, color: 'text.secondary' }}>
                              {blocked ? lever.requiresNote : lever.hint}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : null}

                  {t.key === 'risk' ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mt: 1.25 }}>
                      <TextField
                        label="Time stop"
                        size="small"
                        value={management.maxBarsInTrade === 0 ? '' : management.maxBarsInTrade}
                        onChange={(e) => setLever('maxBarsInTrade', e.target.value)}
                        placeholder="off"
                        /*
                         * SIZED ON THE FIELD, never on the inner <input>.
                         *
                         * FOUND IN A BROWSER: with the width on `inputProps.style` the FormControl
                         * itself had no width of its own, so as a flex child it shrank to 21px and
                         * clipped its floating label to clientWidth 0 — a narrow unlabelled box
                         * next to four lines of prose. The label is positioned against the
                         * CONTROL, so only the control's width can reserve room for it.
                         */
                        sx={{ width: 118, flex: '0 0 auto' }}
                        inputProps={{
                          'data-testid': 'mgmt-max-bars',
                          inputMode: 'numeric',
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {/*
                          EMPTY IS OFF, and the field shows empty rather than 0 for it. Zero is the
                          engine's own disabled value, so there is no separate switch to keep in
                          step — a toggle plus a number is two states for one setting, and they
                          disagree the moment one is edited.
                        */}
                        Bars, after which a trade that has NOT reached breakeven is flattened at the
                        close. A protected trade is never timed out. Leave empty to disable.
                        {' '}{SUGGESTED_MAX_BARS} was the best row of a four-point sweep on one
                        symbol — +43.89% gross at PF 1.25 against the control’s +28.65% at 1.18,
                        with the drawdown four points lower — so it is the obvious next measurement
                        rather than a promoted default.
                      </Typography>
                    </Box>
                  ) : null}

                  {/*
                    THE TUNING PARAMETERS, below the mechanics they belong to. Parameters rather
                    than loosened defaults: widening a band in Java would have moved a measured
                    figure for every caller including the CLI, with no way back to it. These change
                    sensitivity for THIS run and leave every published figure reproducible from a
                    bare command.
                  */}
                  {fields.length ? (
                    <Box
                      data-testid={`mgmt-tuning-${t.key}`}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: 1.25,
                        mt: 1.5,
                      }}
                    >
                      {fields.map((field) => {
                        // `resolveRequires` exists for the one lever whose gate is
                        // THREE-VALUED: an unset committee switch means ON for PTB+Timing, and
                        // `!management.committeeOverride` would read that as off and grey out a
                        // field the run will honour.
                        const blocked = !!field.requires && !(field.resolveRequires
                          ? field.resolveRequires(management) : management[field.requires]);
                        const value = management[field.key];
                        return (
                          <TextField
                            key={field.key}
                            label={field.label}
                            size="small"
                            disabled={blocked}
                            // The raw string is kept, exactly as the time stop does, so a
                            // half-typed `0.` survives the round trip. `normaliseManagement` is
                            // the one place it is coerced, and it is coerced once, on the way out.
                            required={field.key === 'fanFractalWidth' && !blocked}
                            error={field.key === 'fanFractalWidth' && !blocked
                              && !(Number(value) > 0)}
                            value={value === 0 || value === undefined ? '' : value}
                            onChange={(e) => setLever(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            sx={{ width: field.width, flex: '0 0 auto' }}
                            inputProps={{ 'data-testid': field.testId, inputMode: 'decimal' }}
                          />
                        );
                      })}
                    </Box>
                  ) : null}

                  {/*
                    THE NOTES FLOW INTO AS MANY COLUMNS AS FIT, rather than a fixed two.
                    ---------------------------------------------------------------
                    A caption running the full 1,113px is over 150 characters a line, which is
                    past the point prose stops being scannable -- so there is a MINIMUM width
                    and the count follows from it. `auto-fit` is what takes the Risk tab's three
                    notes from two rows to one, which is the 26px that had it alone of the three
                    still over the fold. Each stays its OWN paragraph.
                  */}
                  {t.notes.length ? (
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(auto-fit, minmax(320px, 1fr))',
                      },
                      columnGap: 3,
                      rowGap: 0.5,
                      mt: 0.75,
                    }}>
                      {t.notes.map((note) => (
                        <Typography key={note.slice(0, 24)} variant="caption"
                          sx={{ display: 'block', color: 'text.secondary' }}>
                          {note}
                        </Typography>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>

            {/* FULL WIDTH, under both columns and next to the button it explains. The
                reason Launch is refused belongs beside Launch, not at the foot of whichever
                column happened to be taller. */}
            <Box sx={{ gridColumn: { md: '1 / -1' } }}>
              {problem ? (
                <Alert severity="info" sx={{ mt: 1 }} data-testid="session-problem">
                  {problem}
                </Alert>
              ) : null}
            </Box>
        </Box>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={!!launching}>Cancel</Button>
        <Button
          variant="contained"
          data-testid="session-launch"
          onClick={() => onLaunch(session)}
          disabled={!!problem || !!launching}
          startIcon={launching
            ? <CircularProgress size={16} color="inherit" />
            : <PlayArrowIcon fontSize="small" />}
        >
          {launching ? 'Loading…' : WORKBENCH_LAUNCH}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SimulatorSetupDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Called with the session object. The caller fetches; this dialog does no I/O. */
  onLaunch: PropTypes.func.isRequired,
  /** The chart's CURRENT symbol — the selector's value, and what the session runs on. */
  symbol: PropTypes.string,
  /** The stored symbols to offer. Z-Custom is added by this component, not by the caller. */
  symbols: PropTypes.array,
  /** Changes the CHART's symbol, immediately. Absent disables the selector. */
  onSymbolChange: PropTypes.func,
  /** Receives parsed bars from the upload — the same callback the chart's own panel uses. */
  onCustomSeries: PropTypes.func,
  onClearCustomSeries: PropTypes.func,
  /** The chart's dates, for the window bounds and the default. */
  dates: PropTypes.array,
  launching: PropTypes.bool,
  error: PropTypes.string,
};

SimulatorSetupDialog.defaultProps = {
  open: false,
  symbol: null,
  symbols: null,
  onSymbolChange: null,
  onCustomSeries: null,
  onClearCustomSeries: null,
  dates: null,
  launching: false,
  error: null,
};
